import * as yup from 'yup';
import { DROPDOWN_OPTIONS } from '../../../../../AdviserSettings/Page/CustomModelAssumptions/constants';
import { OptionType } from '../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { PlanStartYearDTO } from '../../../../../ClientSettings/api/useClientSettingsQuery';

export type AssumptionVariant = 'percentage' | 'integer' | 'age' | 'select';

export interface AssumptionDefinition {
  subFieldName: string;
  label: string;
  variant: AssumptionVariant;
  options?: OptionType<number>[];
  canInheritFromFirm?: boolean;
  toolTip?: string;
}

export const ASSUMPTIONS_DEFINITIONS: AssumptionDefinition[] = [
  {
    subFieldName: 'inflationRate',
    label: 'Inflation Rate',
    variant: 'percentage',
  },
  {
    subFieldName: 'loanBalanceInterestRate',
    label: 'Loan Balance Interest Rate',
    variant: 'percentage',
  },
  {
    subFieldName: 'monteCarloSimulations',
    label: 'Monte Carlo Simulations',
    variant: 'integer',
  },
  {
    subFieldName: 'incomeGrowthRate',
    label: 'Income Growth Rate',
    variant: 'percentage',
  },
  {
    subFieldName: 'turnoverRatio',
    label: 'Turnover Ratio',
    variant: 'percentage',
  },
  {
    subFieldName: 'potentialRevenueRate',
    label: 'Potential Revenue Rate',
    variant: 'percentage',
  },
  {
    subFieldName: 'socialSecurityInflationRate',
    label: 'Social Security Inflation Rate',
    variant: 'percentage',
  },
  {
    subFieldName: 'capitalGainsRatio',
    label: 'Long-Term Capital Gains Ratio',
    variant: 'percentage',
  },
  {
    subFieldName: 'excessCashFlowToSave',
    label: 'Excess Cash Flow Savings Rate',
    variant: 'percentage',
    toolTip: 'Enter a value between 0�100, where 0% = Spend, 1-100% = Save',
  },
  {
    subFieldName: 'tuitionInflationRate',
    label: 'Tuition Inflation Rate',
    variant: 'percentage',
  },
  {
    subFieldName: 'lifeExpectancy',
    label: 'Life Expectancy (years)',
    variant: 'age',
  },
  {
    subFieldName: 'portfolioReturnsMethodology',
    label: 'Portfolio Returns',
    variant: 'select',
    options: [
      { label: 'Weighted Average', value: 0 },
      { label: 'Hypothetical', value: 1 },
    ],
  },
  {
    subFieldName: 'riskToleranceWorkflow',
    label: 'Risk Questionnaire',
    variant: 'select',
    canInheritFromFirm: true,
    options: [
      { label: 'Orion Questionnaire', value: 0 },
      { label: '3D Risk Profile', value: 1 },
    ],
  },
  {
    subFieldName: 'capitalMarketAssumptions',
    label: 'Capital Market Assumptions',
    variant: 'select',
    canInheritFromFirm: true,
    options: DROPDOWN_OPTIONS,
  },
  {
    subFieldName: 'planStartYear',
    label: 'Plan Start Year',
    variant: 'select',
    canInheritFromFirm: true,
    options: [
      {
        label: `Current Calendar Year (${new Date().getFullYear()})`,
        value: PlanStartYearDTO.CurrentYear,
        subLabel: `Starts with this year's remaning months`,
      },
      {
        label: `Next Full Year (${new Date().getFullYear() + 1})`,
        value: PlanStartYearDTO.NextYear,
        subLabel: 'Begins projections in next full year',
      },
    ],
  },
  {
    subFieldName: 'monteCarloSuccessRange',
    label: 'Monte Carlo Success Range',
    variant: 'percentage',
    toolTip: 'The default success range is 70 - 99%',
  },
];

export const assumptionsValidation = yup.object().shape({
  monteCarloSuccessRange: yup
    .number()
    .typeError('The value must be between 50% and 99%')
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
    .min(50, 'The value must be between 50% and 99%')
    .max(99, 'The value must be between 50% and 99%'),
  excessCashFlowToSave: yup
    .number()
    .typeError('The value must be between 0% and 100%')
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
    .min(0, 'The value must be between 0% and 100%')
    .max(100, 'The value must be between 0% and 100%'),
});
